import React, { useEffect, useState, useContext } from "react";
import { InvestmentDTO } from "../../model/investmentDTO";
import { getYearList } from "../../model/DatePresetsModel";
import { DatePresetsContext } from "../Store/DatePresetsContext";
import { oneYearTerm, threeYearTerm, sixYearTerm, TermLength, allIndices } from "../../model/CalculatorInputs";
import { CalculatorStepsContext } from "../Store/CalculatorStepsContext";
import { getStrategyConfig } from "../../model/CalculatorInputsValidation";
import { pipe } from "fp-ts/lib/pipeable";
import { fold, none, isNone, isSome } from "fp-ts/lib/Option";
import { fetchSecondaryResults } from "../CalculatorResults/FetchResults";
import { CalculatorResultsContext } from "../Store/CalculatorResultsContext";
import { InvestmentIndex } from '../../model/CalculatorInputs';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';


import '../CalculatorTool/LoadingModal.scss';
import { CalculatorResponse } from "../../model/CalculatorResponse";
import { TooltipComponent } from "../../utils/TooltipComponent/TooltipComponent";
import { wcmUrls } from "../../shared/constants";

interface Props {
    data: InvestmentDTO;
}

function toTermLength(years: number): TermLength {
    if (years === 1) {
        return oneYearTerm;
    } else if (years === 3) {
        return threeYearTerm;
    } else if (years === 6) {
        return sixYearTerm;
    } else {
        return { years: years } as TermLength;
    }
}

function toInvestmentIndex(code: string): InvestmentIndex {
    for (const invIndex of allIndices) {
        if (code === invIndex.code) {
            return invIndex;
        }
    }
    return { code: code, label: code };
}

export const TopMarketComponent = (props: Props) => {
    const datePresetStore = React.useContext(DatePresetsContext);
    const resultsStore = useContext(CalculatorResultsContext);

    const [yearList, setYearList] = useState<Array<string>>([]);


    const customStartYear = props.data.valuesCustomMarket.illustrationModelStartYear;

    const [selectedTermDate, setSelectedTermDate] = useState<string>(customStartYear);

    const { steps, setSteps } = useContext(CalculatorStepsContext);

    const  [strategyType, setStrategyType ] = useState<string>();

    useEffect(() => {
        if (props.data) {
            const invIndex = toInvestmentIndex(props.data.calculatorInputsDTO.index);
            const term = toTermLength(props.data.calculatorInputsDTO.term);
            const cfgRange = steps.config.availableDateRanges.getRangeForInvestmentIndexAndTerm(invIndex, term)
            const years = getYearList(cfgRange.startYear, cfgRange.endYear);
            years.sort().reverse();
            setYearList(years.map(y => y.toString()));

            const customStartYear = props.data.valuesCustomMarket.illustrationModelStartYear;
            setSelectedTermDate(customStartYear);

            const strategyType = props.data.calculatorInputsDTO.strategyType;
            strategyType && setStrategyType(strategyType);
        }
    }, [props.data]);

    function getYearFromResponse(results: CalculatorResponse): string {
        if (results.investments.length > 0) {
            const first = results.investments[0];
            return first.valuesCustomMarket.illustrationModelStartYear;
        } else {
            return 'no-investments';
        }
    }

    useEffect(() => {
        if (isSome(resultsStore.results) && isNone(resultsStore.error)) {
            // user has cleared the custom year error dialog
            pipe(resultsStore.results,
                fold(() => { return; },
                    (results) => {
                        const responseYear = getYearFromResponse(results);
                        if (selectedTermDate !== responseYear) {
                            if (responseYear && responseYear.match(/^\d{4}$/)) {
                                setSelectedTermDate(responseYear);
                            }
                        }
                    }))
        }
    }, [resultsStore.error])


    function getAllocationIndex(productCode: string): number {
        const indicesByCode = new Map<string, number>();
        const availableStrategies = steps.config.availableInvestmentStrategies;
        for (let i = 0; i < steps.inputs.strategyList.getStrategyCount(); i++) {
            const allocatedStrategy = steps.inputs.strategyList.getAllocatedStrategy(i);
            const available = getStrategyConfig(steps.inputs.term, allocatedStrategy.strategy, availableStrategies);
            pipe(available,
                fold(() => { return; },
                    (avStat) => {
                        indicesByCode.set(avStat.productCode, i);
                    }));
        }
        if (indicesByCode.has(productCode)) {
            const index = indicesByCode.get(productCode);
            if (index !== undefined) {
                return index;
            } else {
                return -1;
            }
        } else {
            return -1;
        }
    }

    const handleDatePresetChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const yearString = event.target.value;
        const allocationIndex = getAllocationIndex(props.data.calculatorInputsDTO.productCode);
        if (allocationIndex !== -1 && allocationIndex < steps.inputs.strategyList.getStrategyCount()) {
            setSelectedTermDate(yearString);
            const year = parseInt(yearString);
            const allocatedWithYear = steps.inputs.strategyList.getAllocatedStrategy(allocationIndex).withCustomStartYear(year);
            const newList = steps.inputs.strategyList.withAllocatedStrategyAt(allocationIndex, allocatedWithYear);
            const inputsWithYear = steps.inputs.withStrategyList(newList);
            setSteps(steps.withInputs(inputsWithYear));

            resultsStore.setError(none);
            fetchSecondaryResults(allocationIndex, inputsWithYear, steps.config, resultsStore);
        } else {
            console.log("Changing Date Range: could not refresh data for code " + props.data.calculatorInputsDTO.productCode);
        }

        datePresetStore.setDatePresets(datePresetStore.datePresets.withSelectedDateModel({
            selectedDate: parseInt(yearString),
            investmentIdentifier: props.data.investmentIdentifier
        }))

    }


    const loadingIcon = <FontAwesomeIcon
        data-testid="fa-loading-spinner-icon"
        icon={faSpinner}
        size="1x"
        color="black"
        spin
    />

    const loadingSpinner = (resultsStore.loading)
        ? <span className="icon">{loadingIcon}</span>
        : null;

    const loadingModalClass = "calculator-loading-modal";
    const modalClassNames = (resultsStore.loading)
        ? [loadingModalClass, "visible"].join(' ')
        : [loadingModalClass, "invisible"].join(' ')

    function endYearForTerm(startYear: number, termLengthYears: number) {
        return startYear + termLengthYears - 1;
    }

    function endYearWhileFetchingResponse() {
        const defaultEndYear = props.data?.valuesCustomMarket.illustrationModelEndYear;
        return pipe(resultsStore.results,
            fold(() => { return defaultEndYear; },
                (results) => {
                    return pipe(results.inputs.term,
                        fold(() => { return defaultEndYear; },
                            (term) => {
                                const endYear = endYearForTerm(parseInt(selectedTermDate, 10), term.years);
                                return endYear.toString();
                            }));
                }));
    }

    const endYear = (resultsStore.loading || resultsStore.hasError())
        ? endYearWhileFetchingResponse()
        : props.data?.valuesCustomMarket.illustrationModelEndYear;

    return (
        <>
        { (strategyType === 'dualDirection') ?
        <div className="row top-section-row">
            <div className="top-section">
                <div className="col-xs-9 ml-3">
                    <div className="col-xs-3 section-header text-center">
                        <h5>Down Market (&#x2264; Buffer) <TooltipComponent title="Down market" url={wcmUrls.downMarketTooltip} screenreaderText="More info on Down market (<= Buffer)" /></h5>
                        <span>01/ {props.data?.valuesDownMarketLessThanEqualBuffer.illustrationModelStartYear} - 12/{props.data?.valuesDownMarketLessThanEqualBuffer.illustrationModelEndYear}</span>
                    </div>
                    <div className="col-xs-3 section-header text-center">
                        <h5>Down Market (&#x3E; Buffer) <TooltipComponent title="Down market" url={wcmUrls.downMarketTooltip} screenreaderText="More info on Down market (> Buffer)" /></h5>
                        <span>01/ {props.data?.valuesDownMarketGreaterThanBuffer.illustrationModelStartYear} - 12/{props.data?.valuesDownMarketGreaterThanBuffer.illustrationModelEndYear}</span>
                    </div>
                    <div className="col-xs-3 section-header text-center">
                        <h5>Up Market <TooltipComponent title="Up market" url={wcmUrls.upMarketTooltip} screenreaderText="More info on Up market" /></h5>
                        <span>01/{props.data?.valuesUpMarket.illustrationModelStartYear} - 12/{props.data?.valuesUpMarket.illustrationModelEndYear}</span>
                    </div>
                    <div className="col-xs-3 section-header text-center custom-range-cont">
                        <label htmlFor="customRangeSelect" ><h5>Custom Range</h5></label>
                        <span className="custom-range-text">(Based on the term period selected)</span>
                        <div>01/&nbsp;
                        <select id="customRangeSelect" className="w-25" value={selectedTermDate} onChange={handleDatePresetChange} autoComplete="off">
                                {yearList.map((yearValue) => (<option value={yearValue} key={yearValue} > {yearValue} </option>))}
                            </select> – 12/{endYear} {loadingSpinner}</div>
                    </div>
                </div>
                <div className="col-xs-3"></div>
            </div>                
            <div className={modalClassNames}></div>
        </div> :
        <div className="row top-section-row">
            <div className="col-xs-12 top-section">
                <div className="col-xs-3 section-header text-center">
                    <h5>Down Market <TooltipComponent title="Down market" url={wcmUrls.downMarketTooltip} screenreaderText="More info on Down market" /></h5>

                    <span>01/ {props.data?.valuesDownMarket?.illustrationModelStartYear} - 12/{props.data?.valuesDownMarket?.illustrationModelEndYear}</span>
                </div>
                <div className="col-xs-3 section-header text-center">
                    <h5>Up Market <TooltipComponent title="Up market" url={wcmUrls.upMarketTooltip} screenreaderText="More info on Up market" /></h5>
                    <span>01/{props.data?.valuesUpMarket.illustrationModelStartYear} - 12/{props.data?.valuesUpMarket.illustrationModelEndYear}</span>
                </div>
                <div className="col-xs-3 section-header text-center custom-range-cont">
                    <label htmlFor="customRangeSelect" ><h5>Custom Range</h5></label>
                    <span className="custom-range-text">(Based on the term period selected)</span>
                    <div>01/&nbsp;
                    <select id="customRangeSelect" className="w-25" value={selectedTermDate} onChange={handleDatePresetChange} autoComplete="off">
                            {yearList.map((yearValue) => (<option value={yearValue} key={yearValue} > {yearValue} </option>))}
                        </select> – 12/{endYear} {loadingSpinner}</div>
                </div>
            </div>
            <div className={modalClassNames}></div>
        </div>
        }
        </>
    );
};